import React from "react";
import {
  Heading,
  Text,
  Box,
  Button,
  HStack,
  Container,
  Flex,
  Icon,
  VStack,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FaGithub, FaLinkedin, FaWhatsapp, FaArrowRight } from "react-icons/fa";
import "./Header/styles.css";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionText = motion(Text);
const MotionHeading = motion(Heading);

const socialLinks = [
  { icon: FaGithub, href: "https://github.com/TheIronA", label: "GitHub" },
  { icon: FaLinkedin, href: "https://www.linkedin.com/in/alwi-al-haddad/", label: "LinkedIn" },
  { icon: FaWhatsapp, href: "https://wa.me/601170171030", label: "WhatsApp" }
];

// Dynamic Gradient Background Animation Component
const AnimatedGradientBackground = () => {
  return (
    <MotionBox
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={0}
      opacity={0.06}
      overflow="hidden"
    >
      <MotionBox
        position="absolute"
        width="200%"
        height="200%"
        animate={{
          x: ["-25%", "0%", "-25%"],
          y: ["-25%", "0%", "-25%"],
        }}
        transition={{
          repeat: Infinity,
          repeatType: "reverse",
          duration: 60,
          ease: "linear",
        }}
        bgGradient="radial(circle at 30% 40%, purple.400, transparent 60%),
                    radial(circle at 70% 50%, blue.400, transparent 50%),
                    radial(circle at 20% 70%, teal.300, transparent 70%),
                    radial(circle at 80% 80%, pink.300, transparent 60%)"
      />
    </MotionBox>
  );
};

// Floating decorative elements
const FloatingElement = ({ size, color, top, left, delay }) => {
  return (
    <MotionBox
      position="absolute"
      width={size}
      height={size}
      borderRadius="full"
      backgroundColor={color}
      top={top}
      left={left}
      initial={{ opacity: 0 }}
      animate={{
        opacity: [0.4, 0.7, 0.4],
        y: [0, -15, 0],
        x: [0, 5, 0],
      }}
      transition={{
        duration: 6,
        repeat: Infinity,
        delay: delay,
        ease: "easeInOut",
      }}
    />
  );
};

const LandingSection = () => {
  const handleContactClick = (e) => {
    e.preventDefault();
    const element = document.getElementById('contactme-section');
    const offset = 80;
    const bodyRect = document.body.getBoundingClientRect().top;
    const elementRect = element.getBoundingClientRect().top;
    const elementPosition = elementRect - bodyRect;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };
  const handleProjectsClick = (e) => {
    e.preventDefault();
    const element = document.getElementById('projects-section');
    const offset = 80;
    const bodyRect = document.body.getBoundingClientRect().top;
    const elementRect = element.getBoundingClientRect().top;
    const elementPosition = elementRect - bodyRect;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: (custom) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: custom * 0.2,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    }),
  };

  return (
    <Box
      as="section"
      minH="100vh"
      display="flex"
      alignItems="center"
      bg="brand.primary"
      position="relative"
      overflow="hidden"
      pt={{ base: "28", md: "0" }}
    >
      {/* Animated gradient background */}
      <AnimatedGradientBackground />

      {/* Floating decorative elements */}
      <FloatingElement size="8px" color="rgba(66, 153, 225, 0.6)" top="15%" left="10%" delay={0} />
      <FloatingElement size="12px" color="rgba(159, 122, 234, 0.5)" top="25%" left="85%" delay={1.5} />
      <FloatingElement size="10px" color="rgba(237, 100, 166, 0.5)" top="60%" left="75%" delay={2.3} />
      <FloatingElement size="14px" color="rgba(72, 187, 120, 0.5)" top="80%" left="15%" delay={0.8} />
      <FloatingElement size="6px" color="rgba(237, 137, 54, 0.6)" top="40%" left="45%" delay={3.2} />
      <FloatingElement size="10px" color="rgba(99, 179, 237, 0.5)" top="70%" left="60%" delay={2.7} />

      {/* Background decoration elements */}
      <Box
        position="absolute"
        top="5%"
        left="20%"
        w="300px"
        h="300px"
        borderRadius="full"
        bg="brand.bg.hover"
        filter="blur(80px)"
        opacity="0.1"
        zIndex="0"
      />
      <Box
        position="absolute"
        bottom="10%"
        right="5%"
        w="250px"
        h="250px"
        borderRadius="full"
        bg="rgba(79, 209, 197, 0.2)"
        filter="blur(100px)"
        opacity="0.15"
        zIndex="0"
      />

      {/* Additional color splashes */}
      <Box
        position="absolute"
        top="25%"
        right="15%"
        w="180px"
        h="180px"
        borderRadius="full"
        bgGradient="linear(to-r, purple.400, blue.300)"
        filter="blur(90px)"
        opacity="0.07"
        zIndex="0"
      />
      <Box
        position="absolute"
        bottom="30%"
        left="10%"
        w="220px"
        h="220px"
        borderRadius="full"
        bgGradient="linear(to-br, pink.300, red.200)"
        filter="blur(100px)"
        opacity="0.06"
        zIndex="0"
      />
      <Box
        position="absolute"
        top="60%"
        left="30%"
        w="150px"
        h="150px"
        borderRadius="full"
        bgGradient="linear(to-tr, cyan.200, teal.300)"
        filter="blur(85px)"
        opacity="0.08"
        zIndex="0"
      />
      <Box
        position="absolute"
        top="40%"
        right="30%"
        w="120px"
        h="120px"
        borderRadius="full"
        bgGradient="linear(to-bl, yellow.200, orange.200)"
        filter="blur(70px)"
        opacity="0.05"
        zIndex="0"
      />

      <Container maxW="container.xl" position="relative" zIndex="1" px={{ base: 6, md: 8 }}>
        <Box
          display="grid"
          gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }}
          gap={{ base: "8", md: "12" }}
          alignItems="center"
        >
          {/* Left side - Text content */}
          <VStack
            spacing={6}
            align="flex-start"
            maxW="600px"
            order={{ base: 2, md: 1 }}
          >
            <MotionHeading
              as="h1"
              fontSize={{ base: "3xl", md: "5xl" }}
              fontWeight="bold"
              lineHeight={{ base: "1.3", md: "1.2" }}
              initial="hidden"
              animate="visible"
              custom={0}
              variants={fadeIn}
            >
              <Box as="span" color="brand.text.accent">Hello, I am <br></br>Alwi Al-Haddad!</Box>
            </MotionHeading>

            <MotionHeading
              as="h2"
              fontSize={{ base: "xl", md: "2xl" }}
              fontWeight="medium"
              lineHeight="1.4"
              opacity="0.95"
              initial="hidden"
              animate="visible"
              custom={1}
              variants={fadeIn}
            >
              A Full Stack Developer
              <Box as="span" color="brand.accent"> specializing in</Box> React and Machine Learning
            </MotionHeading>

            <MotionText
              fontSize={{ base: "md", md: "lg" }}
              color="brand.text.secondary"
              maxW="500px"
              initial="hidden"
              animate="visible"
              custom={2}
              variants={fadeIn}
              lineHeight="1.7"
            >
              Graduated with an MEng in Biomedical Engineering from UCL (University College London)
            </MotionText>            <MotionFlex
              direction={{ base: "column", sm: "row" }}
              gap={{ base: 4, sm: 4 }}
              w={{ base: "full", sm: "auto" }}
              initial="hidden"
              animate="visible"
              custom={3}
              variants={fadeIn}
              flexWrap="wrap"
              align="center"
              justify="center"
            >
              <Button
                as="a"
                href="#contactme-section"
                size={{ base: "lg", md: "lg" }}
                colorScheme="blue"
                onClick={handleContactClick}
                rightIcon={<FaArrowRight />}
                px={8}
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "lg",
                }}
                fontSize="md"
                width={{ base: "full", sm: "auto" }}
              >
                Get in Touch
              </Button>
              <Button
                as="a"
                href="#projects-section"
                onClick={handleProjectsClick}
                variant="outline"
                size={{ base: "lg", md: "lg" }}
                borderColor="brand.text.primary"
                color="brand.text.primary"
                _hover={{
                  bg: "whiteAlpha.100",
                  transform: "translateY(-2px)",
                }}
                width={{ base: "full", sm: "auto" }}
              >
                View Projects
              </Button>
            </MotionFlex>

            <MotionFlex
              mt={{ base: 6, md: 8 }}
              align="center"
              gap={4}
              wrap="wrap"
              initial="hidden"
              animate="visible"
              custom={4}
              variants={fadeIn}
            >
              <Text fontSize="sm" color="brand.text.muted" fontWeight="medium">
                CONNECT WITH ME
              </Text>

              <HStack spacing={5}>
                {socialLinks.map((social) => (
                  <MotionBox
                    key={social.label}
                    whileHover={{ scale: 1.1, y: -5 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Box
                      as="a"
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="brand.text.accent"
                      fontSize="xl"
                      transition="all 0.3s"
                      _hover={{
                        color: "brand.accent",
                      }}
                    >
                      <Icon as={social.icon} />
                    </Box>
                  </MotionBox>
                ))}
              </HStack>
            </MotionFlex>
          </VStack>

          {/* Right side - Avatar/Image */}
          <MotionBox
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            display="flex"
            justifyContent={{ base: "center", md: "flex-end" }}
            alignItems="center"
            order={{ base: 1, md: 2 }}
            mb={{ base: 6, md: 0 }}
          >
            <Box
              position="relative"
              w={{ base: "280px", sm: "320px", md: "380px" }}
              h={{ base: "280px", sm: "320px", md: "380px" }}
            >
              {/* Decorative ring */}
              <Box
                position="absolute"
                top="-10px"
                left="-10px"
                right="-10px"
                bottom="-10px"
                borderRadius="full"
                bgGradient="linear(to-br, brand.secondary, brand.accent)"
                opacity="0.2"
                filter="blur(15px)"
              />

              {/* Main image container */}
              <Box
                position="relative"
                w="100%"
                h="100%"
                borderRadius="full"
                bgGradient="linear(to-br, blue.400, blue.500)"
                p="4px"
                overflow="hidden"
                boxShadow="0 20px 40px rgba(0,0,0,0.3)"
                transition="transform 0.3s, box-shadow 0.3s"
                _hover={{
                  boxShadow: "0 25px 50px rgba(49, 130, 206, 0.4)",
                  transform: "translateY(-10px)",
                }}
              >
                <Box
                  w="100%"
                  h="100%"
                  borderRadius="full"
                  overflow="hidden"
                  bg="brand.subtle"
                >
                  <Box
                    as="img"
                    src="/images/profile2.jpg"
                    alt="Alwi Al-haddad"
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                </Box>
              </Box>

              {/* Replace the floating badges with the new scroll-animated badge component */}
              <Box position="absolute" bottom="10%" right="-5%">
              </Box>

              <Box position="absolute" top="5%" left="-5%">
              </Box>
            </Box>
          </MotionBox>
        </Box>
      </Container>
    </Box>
  );
};

export default LandingSection;
